import React, { useEffect, useState } from 'react';
import { Alert, Form, Modal, Radio, Typography } from 'antd';
import { isVllmTaskType } from '@/features/trainings/constants';
import { data_from_server_post } from '@/shared/http/client';

const { Paragraph } = Typography;

const DOWNLOAD_ARTIFACT_TYPES = ['zip', 'docker'];
const PUSH_ARTIFACT_TYPES = ['ecr', 'gcr', 'dockerhub', 'acr'];

const PUSH_ARTIFACT_LABELS = {
    ecr: 'Push to ECR',
    gcr: 'Push to GCR',
    dockerhub: 'Push to Docker Hub',
    acr: 'Push to Azure ACR',
};

/**
 * Returns null if the provider is configured, or a short error string if not.
 * `settings` is the raw get_organization_settings response (may be null while loading).
 */
function missingConfigMessage(artifactType, settings) {
    if (!settings) return null; // still loading — don't block
    switch (artifactType) {
        case 'ecr':
            if (!(settings.aws_ecr_targets || []).some((t) => t?.uri))
                return 'Configure AWS – ECR targets in Account Settings first.';
            return null;
        case 'gcr':
            if (!(settings.gcp_gcr_targets || []).some((t) => t?.uri))
                return 'Configure GCP – GCR targets in Account Settings first.';
            return null;
        case 'dockerhub':
            if (
                !(settings.dockerhub_username || '').trim() ||
                !(settings.dockerhub_targets || []).some((t) => t?.repository)
            )
                return 'Configure Docker Hub credentials and targets in Account Settings first.';
            return null;
        case 'acr':
            if (!(settings.azure_acr_targets || []).some((t) => t?.uri))
                return 'Configure Azure – ACR targets in Account Settings first.';
            return null;
        default:
            return null;
    }
}

/**
 * Format-picker modal: lets the user choose between local download formats
 * (Model raw files / Docker image) or registry push destinations
 * (ECR / GCR / Docker Hub / Azure ACR).
 *
 * ``mode`` controls which set of options is shown:
 *  - ``'all'`` (default): download formats plus configured push targets —
 *    the shared behavior of every Download entry point.
 *  - ``'download'``: ZIP and Docker image only.
 *  - ``'push'``: Registry push targets only.
 *
 * Push targets are only rendered once org settings are loaded, and only for
 * providers the organization has credentials/targets configured for.
 *
 * On confirm we bubble ``{ artifactType }`` up so the parent opens
 * ``ConfigureDeploymentModal`` next.
 */
export default function ModelDownloadModal({
  open,
  onCancel,
  onConfirm,
  modelLabel,
  taskType,
  initialArtifactType,
  mode = 'all',
}) {
  const isVllmTask = isVllmTaskType(taskType);
  const isPushMode = mode === 'push';
  const showDownloadOptions = mode !== 'push';
  const showPushOptions = mode !== 'download';

  const [artifactType, setArtifactType] = useState(isPushMode ? 'ecr' : 'zip');
  const [pushSettings, setPushSettings] = useState(null);
  const [configError, setConfigError] = useState(null);

  // Push targets are hidden until org settings arrive, then narrowed to the
  // providers that are actually configured.
  const configuredPushTypes =
    showPushOptions && pushSettings
      ? PUSH_ARTIFACT_TYPES.filter((t) => !missingConfigMessage(t, pushSettings))
      : [];

  const validTypes = [
    ...(showDownloadOptions ? DOWNLOAD_ARTIFACT_TYPES : []),
    ...configuredPushTypes,
  ];
  // Types the mode may show; used to keep a push selection alive while org
  // settings are still loading (e.g. reopening via Back from Configure).
  const potentialTypes = [
    ...(showDownloadOptions ? DOWNLOAD_ARTIFACT_TYPES : []),
    ...(showPushOptions ? PUSH_ARTIFACT_TYPES : []),
  ];

  // Reset state whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    const candidate = initialArtifactType;
    if (candidate && potentialTypes.includes(candidate)) {
      setArtifactType(candidate);
    } else {
      setArtifactType(isPushMode ? 'ecr' : 'zip');
    }
    setConfigError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialArtifactType, mode]);

  // Once org settings are known, snap an unconfigured selection to the first
  // available option (push mode thus defaults to the first configured target).
  useEffect(() => {
    if (!open) return;
    if (showPushOptions && pushSettings === null) return;
    if (artifactType && validTypes.includes(artifactType)) return;
    setArtifactType(validTypes[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pushSettings]);

  // Fetch org settings once when the modal opens with push options so we can
  // validate whether the selected provider is configured.
  useEffect(() => {
    if (!open || !showPushOptions) return undefined;
    let cancelled = false;
    setPushSettings(null);
    data_from_server_post(
      'get_organization_settings',
      {},
      (result) => { if (!cancelled) setPushSettings(result); },
      () => { if (!cancelled) setPushSettings({}); },
    );
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showPushOptions]);

  // Clear config error when user changes selection.
  const handleTypeChange = (value) => {
    setArtifactType(value);
    setConfigError(null);
  };

  const handleOk = () => {
    if (!artifactType || !validTypes.includes(artifactType)) return;
    if (PUSH_ARTIFACT_TYPES.includes(artifactType)) {
      const err = missingConfigMessage(artifactType, pushSettings);
      if (err) {
        setConfigError(err);
        return;
      }
    }
    onConfirm({ artifactType });
  };

  const dockerLabel = isVllmTask ? 'Docker image' : 'Docker image';
  const title = isPushMode ? 'Push model' : 'Download model';
  const pushSettingsLoaded = pushSettings !== null;
  const noPushTargetConfigured =
    isPushMode && pushSettingsLoaded && configuredPushTypes.length === 0;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Next"
      okButtonProps={{ disabled: !artifactType || !validTypes.includes(artifactType) }}
      destroyOnClose
      zIndex={2100}
    >
      {modelLabel ? (
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {modelLabel}
        </Paragraph>
      ) : null}

      <Form layout="vertical">
        <Form.Item label="Format">
          <Radio.Group
            value={artifactType}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            {showDownloadOptions && (
              <>
                <Radio value="zip">Model raw files</Radio>
                <Radio value="docker">{dockerLabel}</Radio>
              </>
            )}
            {configuredPushTypes.map((type) => (
              <Radio key={type} value={type}>{PUSH_ARTIFACT_LABELS[type]}</Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </Form>

      {noPushTargetConfigured ? (
        <Alert
          type="info"
          message="No registry push targets are configured. Add push targets in Account Settings first."
          showIcon
          style={{ marginTop: 4 }}
        />
      ) : null}

      {configError ? (
        <Alert type="error" message={configError} showIcon style={{ marginTop: 4 }} />
      ) : null}
    </Modal>
  );
}
